/* @skip-file-for-translation */
import invariant from 'invariant';
import { PermissionType } from '@watershed/constants/permissions';

import { plural, t } from '@lingui/core/macro';
import {
  getPermissionTypeDisplayName,
  getPermissionDescription,
} from './permissionsDisplayUtils';
import { SupportedLocale } from '@watershed/intl/constants';

import { formatList } from '@watershed/intl/formatters';

export function formatObjectName(
  locale: SupportedLocale,
  objectName?: string
): string {
  return (
    objectName?.toLocaleLowerCase(locale) ||
    t({ message: 'unknown', context: 'Unknown object name' })
  );
}

export function formatAddPermissionDescription(
  items: ReadonlyArray<{
    permission: PermissionType;
    objectName?: string;
  }>,
  locale: SupportedLocale
): string {
  // TODO: Allow cross permission type assignment?
  invariant(
    items.every((item) => item.permission === items[0].permission),
    'Permissions not all matching'
  );
  const firstPermission = items[0].permission;

  switch (firstPermission) {
    case PermissionType.ApproveDatasource:
    case PermissionType.ManageDatasource:
      if (items[0].objectName) {
        const formattedItems = items.map((item) =>
          formatObjectName(locale, item.objectName)
        );
        const listOfItems = formatList(formattedItems, {
          style: 'short',
          type: 'conjunction',
          locale,
        });

        return plural(items.length, {
          one: `${listOfItems} data source`,
          other: `${listOfItems} data sources`,
        });
      }
      return firstPermission === PermissionType.ApproveDatasource
        ? t`View and approve all data sources`
        : t`Upload and view summaries for all data sources`;
    case PermissionType.ApproveDataset:
    case PermissionType.ManageDataset:
      if (items[0].objectName) {
        const formattedItems = items.map((item) =>
          formatObjectName(locale, item.objectName)
        );
        const listOfItems = formatList(formattedItems, {
          style: 'short',
          type: 'conjunction',
          locale,
        });
        return plural(items.length, {
          one: `${listOfItems} dataset`,
          other: `${listOfItems} datasets`,
        });
      }
      return firstPermission === PermissionType.ApproveDataset
        ? t`View and approve all datasets`
        : t`Upload and view summaries for all datasets`;

    default:
      const permissionDisplayName =
        getPermissionTypeDisplayName(firstPermission);
      const permissionDescription = getPermissionDescription(firstPermission);
      return (
        permissionDescription ?? t`Grant users ${permissionDisplayName} access`
      );
  }
}
